import { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import type { CreateTaskPayload } from "../api/tasks";
import type { TaskMeta } from "../types/tasks";

type Props = {
  open: boolean;
  meta: TaskMeta;
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (payload: CreateTaskPayload) => Promise<void>;
};

export function TaskCreateModal({
  open,
  meta,
  submitting,
  error,
  onClose,
  onSubmit,
}: Props) {
  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [prioridad, setPrioridad] = useState<"baja" | "media" | "alta">("media");
  const [asignadoId, setAsignadoId] = useState("");
  const [proyectoId, setProyectoId] = useState("");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [attachments, setAttachments] = useState<File[]>([]);

  useEffect(() => {
    if (!open) return;

    setTitulo("");
    setDescripcion("");
    setPrioridad("media");
    setAsignadoId("");
    setProyectoId("");
    setFechaInicio("");
    setFechaFin("");
    setAttachments([]);
  }, [open]);

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-white/35 backdrop-blur-[2px]"
        onClick={onClose}
      />

      <div className="fixed inset-0 z-50 grid place-items-center p-4">
        <div className="w-full max-w-3xl overflow-hidden rounded-[36px] border border-[#dfe8e4] bg-white shadow-[0_44px_120px_rgba(15,23,42,.24)]">
          <header className="flex items-start justify-between gap-4 border-b border-[#dfe8e4] bg-[linear-gradient(135deg,#ffffff_0%,#f8fbf9_100%)] px-6 py-5">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-[#00d09c]/18 bg-[#00d09c]/8 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-[#0b4a2f]">
                <Plus size={14} />
                Crear tarea
              </div>
              <h2 className="mt-3 text-2xl font-black tracking-tight text-slate-950">
                Nueva tarea operativa
              </h2>
              <p className="mt-2 text-sm font-semibold text-slate-500">
                Alta guiada con proyecto, responsable, prioridad, fechas y adjuntos iniciales.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="grid h-11 w-11 place-items-center rounded-2xl border border-slate-200 bg-white text-slate-500"
            >
              <X size={17} />
            </button>
          </header>

          <form
            className="grid gap-4 p-6 md:grid-cols-2"
            onSubmit={(event) => {
              event.preventDefault();
              void onSubmit({
                titulo,
                descripcion,
                prioridad,
                asignado_id: asignadoId ? Number(asignadoId) : null,
                proyecto_id: proyectoId ? Number(proyectoId) : null,
                fecha_inicio: fechaInicio || null,
                fecha_fin: fechaFin || null,
                attachments,
              });
            }}
          >
            <label className="md:col-span-2">
              <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                Titulo
              </span>
              <input
                value={titulo}
                onChange={(event) => setTitulo(event.target.value)}
                className="field-control"
                placeholder="Ej. Revisar entregable del proyecto X"
                required
              />
            </label>

            <label className="md:col-span-2">
              <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                Descripcion
              </span>
              <textarea
                value={descripcion}
                onChange={(event) => setDescripcion(event.target.value)}
                className="field-control min-h-[140px]"
                placeholder="Contexto, alcance, dependencias o criterios de revision..."
              />
            </label>

            <label>
              <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                Responsable
              </span>
              <select
                value={asignadoId}
                onChange={(event) => setAsignadoId(event.target.value)}
                className="field-control"
              >
                <option value="">Sin asignar</option>
                {meta.users.map((user) => (
                  <option key={user.id} value={String(user.id)}>
                    {user.nombre}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                Prioridad
              </span>
              <select
                value={prioridad}
                onChange={(event) =>
                  setPrioridad(event.target.value as "baja" | "media" | "alta")
                }
                className="field-control"
              >
                <option value="alta">Alta</option>
                <option value="media">Media</option>
                <option value="baja">Baja</option>
              </select>
            </label>

            <label>
              <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                Proyecto
              </span>
              <select
                value={proyectoId}
                onChange={(event) => setProyectoId(event.target.value)}
                className="field-control"
              >
                <option value="">Sin proyecto</option>
                {meta.projects.map((project) => (
                  <option key={project.id} value={String(project.id)}>
                    {project.nombre}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                Fecha inicio
              </span>
              <input
                type="date"
                value={fechaInicio}
                onChange={(event) => setFechaInicio(event.target.value)}
                className="field-control"
              />
            </label>

            <label>
              <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                Fecha fin
              </span>
              <input
                type="date"
                value={fechaFin}
                onChange={(event) => setFechaFin(event.target.value)}
                className="field-control"
              />
            </label>

            <label className="md:col-span-2">
              <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                Adjuntos iniciales
              </span>
              <input
                type="file"
                multiple
                onChange={(event) =>
                  setAttachments(Array.from(event.target.files ?? []))
                }
                className="field-control"
              />
              {attachments.length > 0 && (
                <div className="mt-2 text-xs font-semibold text-slate-500">
                  {attachments.length} archivo(s) preparados para subir.
                </div>
              )}
            </label>

            {error && (
              <div className="md:col-span-2 rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
                {error}
              </div>
            )}

            <div className="md:col-span-2 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-full border border-[#dfe8e4] bg-white px-5 py-3 text-sm font-black text-slate-600"
              >
                Cancelar
              </button>

              <button
                type="submit"
                disabled={submitting || !titulo.trim()}
                className="rounded-full bg-[linear-gradient(135deg,#0b4a2f_0%,#0f5b3b_50%,#00a879_100%)] px-5 py-3 text-sm font-black text-white shadow-[0_18px_40px_rgba(11,74,47,.22)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? "Creando..." : "Crear tarea"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
